import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, CalendarIcon, CheckCircleIcon, MagnifyingGlassIcon, LinkIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import PageHeader from '../components/layout/PageHeader';
import { useEndpoints, useDeleteEndpoint } from '../hooks/useEndpoints';
import { useListState } from '../hooks/useListState';
import { useSortableData } from '../hooks/useSortableData';
import { SearchBar } from '../components/ui/SearchBar';
import { Pagination } from '../components/ui/Pagination';
import { ViewToggle } from '../components/ui/ViewToggle';
import { DataTable, DataTableHead, DataTableBody, DataTableRow, DataTableCell, TableHeader } from '../components/ui/DataTable';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import EndpointCard from '../components/endpoints/EndpointCard';
import EndpointRequestCount from '../components/endpoints/EndpointRequestCount';
import { EmptyState } from '../components/shared/EmptyState';
import { useFlag } from '../hooks/useFeatureFlags';
import { endpointDisplayName } from '../utils/endpointLabel';
import { ServiceStopChip } from '../components/services/ServiceStopChip';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';

export default function EndpointsPage() {
  const navigate = useNavigate();
  const composerEnabled = useFlag('innovate.fe.composer.page');
  // The create affordance is gated by its manifest flag, whose
  // member-and-above cohorts carry the role rule (task 692).
  const canCreate = useFlag('innovate.fe.endpoints.create');
  const { page, limit, search, viewMode, setPage, setLimit, setSearch, setViewMode } = useListState({
    entityType: 'endpoints',
  });
  const [methodFilter, setMethodFilter] = useState<string>('');

  const { data, isLoading, error } = useEndpoints(page, limit, {
    search: search || undefined,
    method: methodFilter || undefined
  });
  const endpoints = data?.data || [];
  const pagination = data?.pagination;

  const { sortedData, sortConfig, handleSort } = useSortableData(endpoints, 'updatedAt', 'desc');

  const deleteEndpoint = useDeleteEndpoint();

  // Reset pagination when method filter changes
  useEffect(() => {
    setPage(1);
  }, [methodFilter, setPage]);

  const getMethodBadgeClass = (method: string) => {
    switch (method) {
      case 'GET':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'POST':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'PUT':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'PATCH':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      case 'DELETE':
        return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const actions = canCreate ? (
    <Link
      to="/endpoints/new"
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
    >
      <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
      Add AI Service
    </Link>
  ) : undefined;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">Error loading endpoints. Please try again.</div>
      </div>
    );
  }

  const getDescription = () => {
    if (!pagination) return 'AI Services mapped to collaborations';
    const count = pagination.total;
    const plural = count === 1 ? 'AI Service' : 'AI Services';
    const methodText = methodFilter ? ` ${methodFilter}` : '';
    const suffix = search ? ' found' : (methodFilter ? '' : ' total');
    return `${count}${methodText} ${plural}${suffix}`;
  };

  return (
    <div>
      <PageHeader
        title="AI Services"
        description={getDescription()}
        actions={actions}
      />

      <div className="mt-6">
        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search AI Services..."
              />
            </div>
            <select
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">All Methods</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
            <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading AI Services...</div>
          </div>
        ) : sortedData.length === 0 ? (
          search ? (
            <EmptyState
              icon={<MagnifyingGlassIcon className="h-12 w-12" />}
              title="No matching AI Services"
              description={`No AI Services found matching "${search}". Try a different search term or clear filters.`}
              action={{
                label: 'Clear Search',
                onClick: () => setSearch(''),
                variant: 'secondary'
              }}
            />
          ) : (
            <div className="text-center py-12 px-4" data-testid="endpoints-empty-state">
              <div className="flex justify-center mb-4">
                <div className="text-gray-400 dark:text-gray-500">
                  <LinkIcon className="h-12 w-12" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                Turn what you want into a working API
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                An endpoint is a live API any application can call to put your AI to work. Describe what you want and Composer assembles one for you, or build it yourself.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {composerEnabled && (
                  <Link
                    to="/composer"
                    data-testid="endpoints-empty-compose"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
                  >
                    Compose one
                  </Link>
                )}
                {canCreate && (
                <Link
                  to="/endpoints/new"
                  data-testid="endpoints-empty-create-manual"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Create manually
                </Link>
                )}
              </div>
            </div>
          )
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {sortedData.map((endpoint) => (
              <EndpointCard key={endpoint.id} endpoint={endpoint} />
            ))}
          </div>
        ) : (
          <DataTable>
            <DataTableHead>
              <SortableTableHeader
                label="Method"
                field="method"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-20"
              />
              <SortableTableHeader
                // The cell under this shows the service's display name (task
                // 784), so the header names that. Sorting stays on `path` — the
                // stable key the API orders by, and the one thing every row has
                // whether or not it has been named.
                label="AI Service"
                field="path"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-1/3 min-w-0"
              />
              <TableHeader className="w-24">Mode</TableHeader>
              <TableHeader className="w-24">Format</TableHeader>
              <SortableTableHeader
                label="Updated"
                field="updatedAt"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-32"
              />
              <TableHeader className="w-24">Requests</TableHeader>
              <TableHeader className="w-20">
                <span className="sr-only">Actions</span>
              </TableHeader>
            </DataTableHead>
            <DataTableBody>
              {sortedData.map((endpoint) => {
                const label = endpointDisplayName(endpoint);
                return (
                <DataTableRow
                  key={endpoint.id}
                  onClick={() => navigate(`/endpoints/${endpoint.id}`)}
                >
                  <DataTableCell>
                    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getMethodBadgeClass(endpoint.method)}`}>
                      {endpoint.method}
                    </span>
                  </DataTableCell>
                  <DataTableCell>
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {label}
                        </div>
                        <ServiceStopChip stop={endpoint.stop} />
                      </div>
                      {endpoint.description && endpoint.description.trim() !== label && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                          {endpoint.description.length > 60 ? endpoint.description.substring(0, 60) + '...' : endpoint.description}
                        </div>
                      )}
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    {endpoint.responseQueued ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                        Async
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-green-500">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        <span className="text-xs">Sync</span>
                      </span>
                    )}
                  </DataTableCell>
                  <DataTableCell className="text-sm text-gray-500 dark:text-gray-400">
                    {endpoint.requestFormat || '-'}
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {new Date(endpoint.updatedAt).toLocaleDateString()}
                    </div>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <EndpointRequestCount endpointId={endpoint.id} />
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/endpoints/${endpoint.id}`);
                        }}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                      >
                        View
                      </button>
                      {/* The one delete affordance (task 864). The inline
                          two-step Confirm/Cancel swap this replaces named
                          nothing that used the resource and offered no way
                          back from a mis-click. */}
                      <DeleteResourceControl
                        flag="innovate.fe.services.delete"
                        entityLabel="AI Service"
                        name={endpointDisplayName(endpoint)}
                        id={endpoint.id}
                        stopPropagation
                        body={<p>Its URL stops answering immediately and its path is free to reuse. Its scenarios and generated interfaces go with it; its collaboration config and the agents it uses stay.</p>}
                        onDelete={() => deleteEndpoint.mutateAsync(endpoint.id)}
                      />
                    </div>
                  </DataTableCell>
                </DataTableRow>
                );
              })}
            </DataTableBody>
          </DataTable>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
